/* eslint-disable spire/export-styles */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { MainNavigationStyles, MappedLink } from "@insite/content-library/Widgets/Header/MainNavigation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Popover from "@insite/mobius/Popover";
import TechnicalSupportToolsContent, {
    LinkItem as TechSupportLinkItem,
} from "@thkStore/Components/TechnicalSupportToolsContent";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement | null>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
    technicalSupportTitle?: string;
    technicalSupportLinks?: TechSupportLinkItem[];
    technicalSupportFootnote1?: string;
    technicalSupportFootnote2?: string;
    technicalSupportButtonText?: string;
    technicalSupportRegistrationUrl?: string;
    technicalSupportMembershipCheckText?: string;
    technicalSupportMembershipCheckUrl?: string;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    state = {
        isLocalOpen: false
    };

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    // Handlers for mouse events
    handleMouseEnter = () => {
        this.setState({ isLocalOpen: true });
    };

    handleMouseLeave = () => {
        this.setState({ isLocalOpen: false });
    };

    handleMegaMouseEnter = () => {
        this.openPopover();
    };

    handleMegaMouseLeave = () => {
        this.closePopover();
    };

    handleCascadingClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        const anchorElement = target.closest("a");
        if (anchorElement && anchorElement.getAttribute("href")) {
            this.setState({ isLocalOpen: false });
        }
    };

    renderCustomContent = () => {
        const {
            technicalSupportTitle,
            technicalSupportLinks,
            technicalSupportFootnote1,
            technicalSupportFootnote2,
            technicalSupportButtonText,
            technicalSupportRegistrationUrl,
            technicalSupportMembershipCheckText,
            technicalSupportMembershipCheckUrl,
        } = this.props;

        return (
            <CustomContentContainer
                onMouseEnter={this.handleMouseEnter}
            >
                <TechnicalSupportToolsContent
                    variant="menu"
                    sectionTitle={technicalSupportTitle}
                    links={technicalSupportLinks}
                    footnoteLine1={technicalSupportFootnote1}
                    footnoteLine2={technicalSupportFootnote2}
                    memberButtonText={technicalSupportButtonText}
                    memberRegistrationUrl={technicalSupportRegistrationUrl}
                    membershipCheckText={technicalSupportMembershipCheckText}
                    membershipCheckUrl={technicalSupportMembershipCheckUrl}
                />
            </CustomContentContainer>
        );
    };

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading =
            hasChildren && (link.childrenType === "CascadingMenu" || link.childrenType === "CustomLinkList");
        const isMega = hasChildren && link.childrenType === "MegaMenu";

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        // Apply custom content to any element with children.
        if (hasChildren) {
            const { isLocalOpen } = this.state;

            if (isCascading && link.children) {
                const menuProps: { maxDepth?: number } = {};
                if (link.maxDepth) {
                    menuProps.maxDepth = link.maxDepth;
                }

                menuItem = (
                    <div
                        style={{ position: 'inherit' }}
                        onMouseEnter={this.handleMouseEnter}
                        onMouseLeave={this.handleMouseLeave}
                        onClick={this.handleCascadingClick}
                    >
                        <Menu
                            className="cascading-menu"
                            data-test-selector={`mainNavigationItem_${index}`}
                            isOpen={isOpen || isLocalOpen || undefined}
                            descriptionId={`${link.title}_${index}`}
                            menuItems={link.children}
                            menuTrigger={menuLink}
                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                            {...menuProps}
                            {...styles.cascadingMenu}
                        />
                        {/* Use JavaScript to insert dynamic content */}
                        {isLocalOpen && (
                            <StyledWrapper>
                                {this.renderCustomContent()}
                            </StyledWrapper>
                        )}
                    </div>
                );
            } else if (isMega) {
                const megaMenuComponent = (
                    <Popover
                        className="mega-menu"
                        zIndexKey="menu"
                        {...styles.megaMenu}
                        contentBodyProps={{
                            ...styles.megaMenu?.contentBodyProps,
                            _width: 1140,
                            as: "div",
                        }}
                        controlsId={`${index}_${link.title}`}
                        ref={this.popover}
                        positionFunction={(element: React.RefObject<HTMLUListElement | null>) => {
                            if (this.props.container.current?.offsetParent) {
                                const parentPosition = this.props.container.current.getBoundingClientRect();
                                const rect = element.current?.getBoundingClientRect();
                                const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                                const cartWidth = 150;
                                const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                    return (
                                        parentPosition.width + cartWidth <= value &&
                                        parentPosition.width + cartWidth > maxWidths[index - 1]
                                    );
                                });

                                const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                                // Center the mega menu relative to the menu item that triggered it.
                                const triggerRect = rect;
                                const triggerCenter = triggerRect ? triggerRect.left + triggerRect.width / 2 : 0;

                                return {
                                    top,
                                    position: "fixed",
                                    left: triggerCenter,
                                    transform: "translateX(-50%)",
                                };
                            }
                            return { position: "absolute" };
                        }}
                        popoverTrigger={menuLink}
                        insideRefs={[this.element]}
                    >
                        <GridContainer
                            className="mega-menu-grid-container"
                            data-test-selector={`mainNavigationItem_${index}`}
                            {...styles.megaMenuGridContainer}
                            offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                        >
                            {link
                                .children!.filter(child => !child.excludeFromNavigation)
                                .map((child, index) => (
                                    <GridItem
                                        className="mega-menu-grid-item"
                                        width={(12 / link.numberOfColumns!) as GridWidths}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        {...styles.megaMenuGridItem}
                                        as="li"
                                    >
                                        <Link
                                            className="mega-menu-heading-link"
                                            {...styles.megaMenuHeading}
                                            href={child.url}
                                            onClick={this.closePopover}
                                        >
                                            {child.title}
                                        </Link>
                                        {child.children && (
                                            <ul data-test-selector={`linksFor_${child.title}`}>
                                                {child.children
                                                    .filter(grandChild => {
                                                        if (grandChild.excludeFromNavigation) {
                                                            return false;
                                                        }
                                                        if (
                                                            grandChild.type === "ChangeCustomerPage" &&
                                                            !this.props.displayChangeCustomerLink
                                                        ) {
                                                            return false;
                                                        }
                                                        if (grandChild.type === "VmiUsersPage") {
                                                            const currentMode = getCookie(NavigationModeCookieName);
                                                            if (!currentMode || currentMode === "Storefront") {
                                                                return false;
                                                            }
                                                        }

                                                        return true;
                                                    })
                                                    .map((grandChild, index) => (
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <li key={index}>
                                                            <Link
                                                                className="mega-menu-link"
                                                                {...styles.megaMenuLink}
                                                                onClick={this.closePopover}
                                                                href={grandChild.url}
                                                                data-test-selector={grandChild.title}
                                                            >
                                                                {grandChild.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </GridItem>
                                ))}
                        </GridContainer>
                    </Popover>
                );

                menuItem = (
                    <div
                        style={{ position: 'relative' }}
                        onMouseEnter={this.handleMegaMouseEnter}
                        onMouseLeave={this.handleMegaMouseLeave}
                    >
                        {megaMenuComponent}
                    </div>
                );
            }
        }

        return menuItem;
    }
}

// Styled component Menu
const CustomContentContainer = styled.div`
    position: absolute;
    top: calc(100% + 32px);
    left: 665px;
    z-index: 1290;
    padding-left: 32px;
    border-left: ${({ theme }) => theme.colors.common.border} solid 1px;

    .gray-box {
        color: ${({ theme }) => theme.colors.secondary.main};
        background: ${({ theme }) => theme.colors.secondary.contrast};
        border-radius: 8px;
        padding: 20px 22px 24px;
        width: 295px;
        height: 479px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        &__links{
            border-bottom: ${({ theme }) => theme.colors.secondary.main} solid 1px;
            margin-bottom: 24px;
        }

        a{
            font-size: 14px;
            display: block;
            width: 100%;
            margin-bottom: 14px;

            &:hover{
                opacity: 0.75;

                span{
                    color: ${({ theme }) => theme.colors.secondary.main};
                }
            }

            & > span{
                color: ${({ theme }) => theme.colors.secondary.main};
                display: flex;
                justify-content: space-between;
                align-items: center;

                & > span{

                    &:first-child{
                        display: flex;
                        align-items: center;
                        gap: 6px;

                        svg{
                            width: 12px;
                            height: 12px;
                        }
                    }
                }
            }

            svg{
                color: ${({ theme }) => theme.colors.secondary.main};
                height: 18px;
                width: 18px;
            }
        }
    }

    h3 {
        margin-bottom: 25px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
    }

    p {
        font-size: 12px;
        text-align: center;
        margin-bottom: 0;
    }

    button {
        width: 100%;
        margin-top: 12px;
        color: ${({ theme }) => theme.colors.secondary.contrast};
        border: ${({ theme }) => theme.colors.secondary.contrast} solid 1px;
        gap: 6px;

        span{

            &:last-child{

                margin-left: 6px;

                    svg{
                        color: ${({ theme }) => theme.colors.secondary.contrast};
                    }
            }
        }
    }

    .more-links{

        a{
            width: 100%;
            margin-top: 16px;

            &:hover{

                span{
                    color: ${({ theme }) => theme.colors.primary.main};
                }
            }

            span{
                color: ${({ theme }) => theme.colors.secondary.contrast};
            }

            & > span{
                color: ${({ theme }) => theme.colors.secondary.main};
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
            }
        }
    }
`;

export default withTheme(MainNavigationItem);
